import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import * as authApi from '@/modules/auth/api';
import type { AuthUser } from '@/modules/auth/api';

/**
 * Session + 2FA state for the whole app.
 *
 * The session itself lives in httpOnly cookies on the API; this store only
 * mirrors the authenticated `me` and orchestrates the passwordless / 2FA
 * mutations. `consumeCode()` / `consumeToken()` return `twoFactorRequired` (no
 * session yet) when the account has 2FA on — the caller then completes
 * `completeOtp()` / `completeRecovery()`.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  /** True once the first `me` probe has resolved (guards the router bootstrap). */
  const loaded = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const isOtpEnabled = computed(() => user.value?.isOtpEnabled === true);
  const otpRecoveryCodesRemaining = computed(() => user.value?.otpRecoveryCodesRemaining ?? 0);
  /** Superadmins are the only role allowed to manage users and their roles. */
  const isSuperadmin = computed(() => user.value?.roles?.some((r) => r.id === 'SUPERADMIN') ?? false);

  function requireUserId(): string {
    if (!user.value) throw new Error('Not authenticated');
    return user.value.id;
  }

  /** Re-probe the current session and refresh the mirrored user. */
  async function refresh(): Promise<void> {
    try {
      const me = (await authApi.fetchMe()).me;
      // The API resolves `me` to a guest sentinel (its id contains "guest") for
      // unauthenticated requests instead of erroring — treat that as logged-out,
      // matching the backend's own guest check.
      user.value = me.id.includes('guest') ? null : me;
    } catch {
      user.value = null;
    } finally {
      loaded.value = true;
    }
  }

  /** Probe `me` exactly once — used by the router guard on first navigation. */
  async function ensureLoaded(): Promise<void> {
    if (!loaded.value) await refresh();
  }

  /**
   * Kick off a passwordless sign-in: the API emails a click-through link plus a
   * 6-digit code. Nothing is returned beyond delivery — the caller advances to
   * the code-entry step.
   */
  async function requestMagicLink(email: string): Promise<void> {
    await authApi.requestMagicLink(email);
  }

  /**
   * First factor via the 6-digit code the user typed back. Resolves
   * `{ twoFactorRequired: true }` when the account has 2FA on (server has set the
   * pending cookie, no session yet); otherwise the session is live and `user` is
   * populated.
   */
  async function consumeCode(email: string, code: string): Promise<{ twoFactorRequired: boolean }> {
    const { twoFactorRequired } = (await authApi.consumeMagicLinkByCode(email, code)).consumeMagicLinkByCode;
    if (twoFactorRequired) {
      return { twoFactorRequired: true };
    }
    // Session is live — reload `me` so the mirrored user carries its roles
    // (the consume mutation can't select `roles`, which is a self-only resolver).
    await refresh();
    return { twoFactorRequired: false };
  }

  /**
   * First factor via the magic-link token (the email's click-through link).
   * Same 2FA branching as `consumeCode`.
   */
  async function consumeToken(token: string): Promise<{ twoFactorRequired: boolean }> {
    const { twoFactorRequired } = (await authApi.consumeMagicLinkByToken(token)).consumeMagicLinkByToken;
    if (twoFactorRequired) {
      return { twoFactorRequired: true };
    }
    await refresh();
    return { twoFactorRequired: false };
  }

  /** Second factor via authenticator code. */
  async function completeOtp(token: string): Promise<void> {
    await authApi.validateOtp(token);
    await refresh();
  }

  /** Second factor via a single-use recovery code. */
  async function completeRecovery(code: string): Promise<void> {
    await authApi.validateOtpWithRecoveryCode(code);
    await refresh();
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout();
    } finally {
      user.value = null;
    }
  }

  // ---- 2FA management (authenticated) ----

  /** Begin enrollment: returns the shared secret + `otpauth://` URL for the QR. */
  function configureOtp(): Promise<{ secret: string; otpAuthUrl: string }> {
    return authApi.configureOtp(requireUserId()).then((r) => r.configureOtp);
  }

  /** Confirm the first code, enable 2FA, and return the one-time recovery codes. */
  async function verifyOtp(token: string): Promise<string[]> {
    const { recoveryCodes } = (await authApi.verifyOtp(requireUserId(), token)).verifyOtp;
    await refresh();
    return recoveryCodes;
  }

  async function disable2fa(opts: { code?: string; recoveryCode?: string }): Promise<void> {
    await authApi.disable2fa({ userId: requireUserId(), ...opts });
    await refresh();
  }

  async function regenerateRecoveryCodes(code: string): Promise<string[]> {
    const { recoveryCodes } = (await authApi.regenerateOtpRecoveryCodes(code)).regenerateOtpRecoveryCodes;
    await refresh();
    return recoveryCodes;
  }

  return {
    user,
    loaded,
    isAuthenticated,
    isOtpEnabled,
    otpRecoveryCodesRemaining,
    isSuperadmin,
    ensureLoaded,
    refresh,
    requestMagicLink,
    consumeCode,
    consumeToken,
    completeOtp,
    completeRecovery,
    logout,
    configureOtp,
    verifyOtp,
    disable2fa,
    regenerateRecoveryCodes,
  };
});
